import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const Header = () => {
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    axios
      .get("/api/users/me/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => setUser(res.data))
      .catch((err) => console.error("Error fetching user info:", err));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("username");
    setUser(null);
    navigate("/login");
  };

  const logoUrl = useMemo(() => {
    const rawLogo = user?.company?.logo_url;
    if (!rawLogo) return null;

    if (rawLogo.startsWith("http://")) {
      return rawLogo.replace("http://", "https://");
    }

    if (rawLogo.startsWith("https://")) {
      return rawLogo;
    }

    if (rawLogo.startsWith("/")) {
      return `${window.location.origin}${rawLogo}`;
    }

    return rawLogo;
  }, [user]);

  const navItems = [
    { label: "Home", path: "/dashboard", match: ["/dashboard"] },
    {
      label: "Policies & Documents",
      path: "/policies",
      match: ["/policies", "/select-policy", "/policy-reviews"],
    },
    {
      label: "Exception Reporting",
      path: "/exception-reporting",
      match: ["/exception-reporting", "/exception-reports"],
    },
    {
      label: "Help & Support",
      path: "/contact-us",
      match: ["/contact-us"],
    },
  ];

  const currentPath = location.pathname;
  const initials = (user?.username || "U").slice(0, 1).toUpperCase();

  return (
    <header className="app-header">
      <div className="app-header__brand">
        <div className="app-header__logo-wrap">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={user?.company?.company_name || "Company logo"}
              className="app-header__logo"
            />
          ) : (
            <div className="app-header__placeholder">
              {user?.company?.company_name || "ISO"}
            </div>
          )}
        </div>

        <div>
          <p className="app-header__eyebrow">ISO Task Management</p>
          <h1 className="app-header__title">
            {user?.company?.company_name || "Compliance Workspace"}
          </h1>
          <p className="app-header__subtitle">
            Professional oversight for actionables, reviews, policies, and meetings
          </p>
        </div>
      </div>

      <nav className="app-header__nav">
        {navItems.map((item) => {
          const isActive = item.match.some((prefix) =>
            currentPath.startsWith(prefix)
          );

          return (
            <button
              key={item.label}
              type="button"
              className={`app-header__nav-button ${isActive ? "is-active" : ""}`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          );
        })}
      </nav>

      {user && (
        <div className="app-header__user" ref={dropdownRef}>
          <button
            type="button"
            className="app-header__user-button"
            onClick={() => setDropdownOpen((prev) => !prev)}
          >
            <span className="app-header__user-badge">{initials}</span>
            <span>{user.username}</span>
          </button>

          {dropdownOpen && (
            <div className="app-header__dropdown">
              <button
                type="button"
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/change-password");
                }}
              >
                Change Password
              </button>
              <button type="button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
